/* Seitenleiste „Ebenen“: Gruppen (anlegen, auflösen, sichtbar, auswählen …), Ebenen nach Art und Sichtbarkeit. */
import { createRoot } from 'react-dom/client';
import Groups from '../../model/groups.ts';
import type { Group, VisState } from '../../model/groups.ts';
import type { Doc, Sel } from '../../model/types.ts';
import { useT } from '../hooks.ts';
import { createNotifier, useNotifier } from '../notifier.ts';
import type { Notifier } from '../notifier.ts';
import { CommitInput } from '../texture/widgets.tsx';

export interface LayersApp {
  doc: Doc; sel: Sel; vis: VisState;
  scheduleSave(): void;
  hiddenSet(): Set<string>;
  groupSel(): void; ungroupSel(): void; groupToggle(id: string): void; selectGroup(id: string): void; groupAdd(id: string): void;
  groupRemove(id: string): void; groupIsolate(id: string): void; groupDelete(id: string): void;
  autoToggle(key: string): void; hideSel(): void; isolateSel(): void; showAll(): void;
}

function GroupRow(props: { app: LayersApp; g: Group; refresh: () => void }) {
  const t = useT();
  const { app, g } = props;
  return (
    <div className={'grow' + (g.hidden ? ' off' : '')}>
      <div className="row">
        <button className="btn sm eye" title={g.hidden ? t('lay.show') : t('ctx.hide')} aria-pressed={!g.hidden} onClick={() => app.groupToggle(g.id)}>{g.hidden ? '○' : '●'}</button>
        <CommitInput value={g.name} aria-label={t('lay.groups')} onCommit={(v) => { g.name = v.trim() || g.name; app.scheduleSave(); props.refresh(); }} />
        <span className="small">{String(g.members.size)}</span>
      </div>
      <div className="row">
        <button className="btn sm" onClick={() => app.selectGroup(g.id)}>{t('ui.tool.select')}</button>
        <button className="btn sm" title={t('lay.add_sel_tip')} onClick={() => app.groupAdd(g.id)}>{t('lay.add_sel')}</button>
        <button className="btn sm" title={t('lay.remove_sel_tip')} onClick={() => app.groupRemove(g.id)}>{t('lay.remove_sel')}</button>
        <button className="btn sm" title={t('lay.only_this_tip')} onClick={() => app.groupIsolate(g.id)}>{t('lay.only_this')}</button>
        <button className="btn sm" title={t('lay.delete_tip')} onClick={() => app.groupDelete(g.id)}>×</button>
        <label className="chk" title={t('lay.sticky_tip')}>
          <input type="checkbox" checked={g.sticky} onChange={(e) => { g.sticky = e.target.checked; app.scheduleSave(); props.refresh(); }} />{t('lay.sticky')}
        </label>
      </div>
    </div>
  );
}

export function LayersPanel(props: { app: LayersApp; notifier: Notifier }) {
  const t = useT();
  useNotifier(props.notifier);
  const { app } = props, V = app.vis, sel = app.sel, hasSel = sel.b.size || sel.e.size;
  const counts = Groups.autoCounts(app.doc), hid = app.hiddenSet();
  return (
    <>
      <h3>{t('lay.groups')}</h3>
      <div className="row">
        <button className="btn sm primary" title={t('lay.group_sel_tip')} onClick={() => app.groupSel()}>{t('lay.group_sel')}</button>
        <button className="btn sm" title={t('lay.dissolve_tip')} onClick={() => app.ungroupSel()}>{t('lay.dissolve')}</button>
      </div>
      <p className="small">{hasSel ? t('lay.sel_counts', { b: sel.b.size, e: sel.e.size }) : t('lay.nothing_sel')}</p>
      {!V.groups.length && <p className="small">{t('lay.no_groups')}</p>}
      {V.groups.map((g) => <GroupRow key={g.id} app={app} g={g} refresh={props.notifier.notify} />)}
      <h3>{t('lay.by_kind')}</h3>
      <p className="small">{t('lay.by_kind_note')}</p>
      {Groups.AUTO.filter((a) => counts[a[0]]).map((a) => (
        <label key={a[0]} className="chk lay">
          <input type="checkbox" checked={!V.auto.has(a[0])} onChange={() => app.autoToggle(a[0])} />
          <span>{t(a[1])}</span><span className="small">{String(counts[a[0]])}</span>
        </label>
      ))}
      <h3>{t('lay.visibility')}</h3>
      <div className="row">
        <button className="btn sm" title="H" onClick={() => app.hideSel()}>{t('lay.hide_sel')}</button>
        <button className="btn sm" title={t('lay.alt_h')} onClick={() => app.isolateSel()}>{t('ctx.isolate')}</button>
        <button className="btn sm primary" title={t('lay.shift_h')} onClick={() => app.showAll()}>{t('lay.show_all')}</button>
      </div>
      <p className="small">{hid.size ? t('lay.hidden_note', { count: hid.size }) : t('lay.all_visible')}</p>
    </>
  );
}

export function mountLayersPanel(el: HTMLElement, app: LayersApp): { refresh(): void; unmount(): void } {
  const notifier = createNotifier();
  const root = createRoot(el);
  el.textContent = '';
  root.render(<LayersPanel app={app} notifier={notifier} />);
  return { refresh: notifier.notify, unmount: () => root.unmount() };
}
